'use client'

import { TabsContactForm } from '@/components/Forms/TabsContactForm.client'
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'
import type { Form } from '@payloadcms/plugin-form-builder/types'
import { Play } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import RichText from '@/components/RichText'
import { ProjectTabList } from '@/components/ProjectTabButton'
import { MediaModal } from '@/components/MediaModal'
import { outfit } from '@/fonts'
import { cn } from '@/utilities/ui'

// Lightbox renders only on open — code-split out of the initial bundle.
const ImageLightbox = dynamic(
  () => import('@/components/ImageLightbox').then((m) => m.ImageLightbox),
  { ssr: false },
)

import type {
  FeaturedVideo,
  FloorplanPlan,
  ProjectShowcaseClientProps,
  ShowcaseTabPayload,
} from './buildProps'

const TEAL = '#1595a7'
/** Matches TabbedSection “Start crafting” / contact form tab accent. */
const ACCENT_BAR = '#4BA3B0'

function TabPlus({ active }: { active: boolean }) {
  return (
    <svg
      viewBox="0 0 512 512"
      className={cn(
        'h-4 w-4 shrink-0',
        active
          ? 'text-[#349BAA]'
          : 'text-black group-hover:text-[#009BAB]! transition-all duration-300',
      )}
      fill="currentColor"
      aria-hidden
    >
      <path d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm144 276c0 6.6-5.4 12-12 12h-92v92c0 6.6-5.4 12-12 12h-56c-6.6 0-12-5.4-12-12v-92h-92c-6.6 0-12-5.4-12-12v-56c0-6.6 5.4-12 12-12h92v-92c0-6.6 5.4-12 12-12h56c6.6 0 12 5.4 12 12v92h92c6.6 0 12 5.4 12 12v56z" />
    </svg>
  )
}

function SecondaryTabButton({
  id,
  label,
  isActive,
  onSelect,
  primaryTabId,
}: {
  id: string
  label: string
  isActive: boolean
  onSelect: () => void
  primaryTabId: string
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      aria-controls={`project-gallery-panel-${primaryTabId}`}
      id={`project-gallery-tab-${primaryTabId}-${id}`}
      tabIndex={isActive ? 0 : -1}
      className={cn(
        'group font-outfit inline-flex min-h-[30px] md:min-h-[44px] flex-1 items-center justify-center gap-2 p-[4px] md:px-0 md:py-2.5 text-center text-[14px] font-bold leading-[21px] md:text-[15px]',
        isActive
          ? 'text-white'
          : 'text-black transition-all duration-300 hover:bg-[#96969647]! hover:text-[#009BAB]! cursor-pointer',
      )}
      style={{ backgroundColor: isActive ? '#72c7d0' : '#F7E8B1' }}
      onClick={onSelect}
    >
      <span className="min-w-0 leading-tight">{label}</span>
      <span className="hidden md:inline-flex">
        <TabPlus active={isActive} />
      </span>
    </button>
  )
}

/**
 * Editorial gallery planner. Given `n` images and `cols` columns, returns the
 * column-span for each image so that EVERY row — including the last — is
 * completely filled (no trailing blank columns). Full rows stay a clean grid;
 * the trailing `n % cols` images are widened to absorb the leftover columns,
 * which reads as an intentional feature row rather than dead space. Pure and
 * deterministic, so it runs identically on server and client.
 */
function rowFillSpans(n: number, cols: number): number[] {
  const spans = new Array(n).fill(1)
  if (n === 0) return spans
  const r = n % cols
  if (r === 0) return spans // exact multiple — uniform grid already fills
  // Distribute the final `r` tiles across `cols` (e.g. r=2,cols=4 → [2,2]).
  const start = n - r
  const base = Math.floor(cols / r)
  const extra = cols % r
  for (let i = 0; i < r; i++) spans[start + i] = base + (i < extra ? 1 : 0)
  return spans
}

// Literal class maps so Tailwind's JIT can see every span variant it must emit.
const BASE_SPAN: Record<number, string> = { 1: 'col-span-1', 2: 'col-span-2' }
const MD_SPAN: Record<number, string> = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
}
const LG_SPAN: Record<number, string> = {
  1: 'lg:col-span-1',
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
  4: 'lg:col-span-4',
}

function PhotoGalleryPanel({
  tab,
  secondaryIndex,
}: {
  tab: ShowcaseTabPayload
  secondaryIndex: number
}) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const categories = tab.galleryCategories
  if (categories.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">
        Add gallery categories for this tab in the project admin.
      </p>
    )
  }

  const cat = categories[Math.min(secondaryIndex, categories.length - 1)]!
  const images = cat.images

  if (images.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">
        No images in &ldquo;{cat.label}&rdquo; yet. Add images in the admin.
      </p>
    )
  }

  // Per-breakpoint span plans so the last row never leaves blank columns.
  const spans2 = rowFillSpans(images.length, 2)
  const spans3 = rowFillSpans(images.length, 3)
  const spans4 = rowFillSpans(images.length, 4)

  return (
    <>
      <div>
        <div
          className="grid grid-cols-2 gap-[10px] auto-rows-[33vw] sm:auto-rows-[220px] md:grid-cols-3 md:auto-rows-[210px] lg:grid-cols-4 lg:auto-rows-[185px]"
          id={`project-gallery-panel-${tab.id}`}
          aria-labelledby={`project-gallery-tab-${tab.id}-${cat.id}`}
        >
          {images.map((img, idx) => (
            <button
              key={`${img.src}-${idx}`}
              type="button"
              aria-label={`View ${img.alt || `image ${idx + 1}`}`}
              className={cn(
                'group relative h-full w-full overflow-hidden bg-[#e8e8e8] shadow-sm cursor-zoom-in',
                BASE_SPAN[spans2[idx]!],
                MD_SPAN[spans3[idx]!],
                LG_SPAN[spans4[idx]!],
              )}
              onClick={() => setLightboxIndex(idx)}
            >
              <Image
                alt={img.alt}
                className="object-cover transition-transform duration-300 group-hover:scale-105"
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 66vw, 50vw"
                src={img.src}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300" />
            </button>
          ))}
        </div>
      </div>

      {lightboxIndex !== null && (
        <ImageLightbox
          images={images}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  )
}

function EmbedPanel({ tours }: { tours: { url: string; label: string }[] }) {
  const [active, setActive] = useState(0)
  const current = tours[Math.min(active, tours.length - 1)]!
  return (
    <div className="flex flex-col items-center gap-3">
      {tours.length > 1 && (
        <div className="flex gap-2 flex-wrap justify-center">
          {tours.map((t, i) => (
            <button
              key={t.url}
              type="button"
              onClick={() => setActive(i)}
              className={cn(
                'px-4 py-1.5 text-sm font-semibold transition-colors',
                i === active ? 'text-white' : 'text-[#4f5555] hover:bg-[#96969647]',
              )}
              style={{ backgroundColor: i === active ? '#72c7d0' : '#F7E8B1' }}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      <div className="w-[600px] max-w-full h-[600px]">
        <iframe
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="h-full w-full border-0"
          src={current.url}
          title={current.label}
        />
      </div>
    </div>
  )
}

function VideosPanel({ src, poster }: { src: string | null; poster: string | null }) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [videoStarted, setVideoStarted] = useState(false)

  if (!src?.trim()) {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">
        Add a direct video file URL (Videos tab → Video file URL) in the project admin.
      </p>
    )
  }

  return (
    <div className="relative flex justify-start w-full">
      <video
        ref={videoRef}
        className="aspect-video h-[600px] max-h-[600px] w-full max-w-[600px] object-contain rounded-sm shadow-sm"
        controls={videoStarted}
        playsInline
        preload="none"
        src={src.trim()}
        poster={poster || undefined}
      />
      {!videoStarted && (
        <button
          type="button"
          aria-label="Play video"
          className="absolute inset-0 flex items-center justify-center cursor-pointer max-w-[600px]"
          onClick={() => {
            setVideoStarted(true)
            videoRef.current?.play().catch(() => {})
          }}
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/85">
            <Play className="ml-0.5 h-5 w-5 fill-[#1d1d1b] text-[#1d1d1b]" />
          </span>
        </button>
      )}
    </div>
  )
}

function CtaFormPanel({
  logoUrl: _logoUrl,
  logoAlt: _logoAlt,
  heading: _heading,
  subHeading: _subHeading,
  form: _form,
}: {
  logoUrl: string | null
  logoAlt: string | null
  heading: string | null
  subHeading: string | null
  form: Form
}) {
  const resolvedLogoUrl = '/logo/logo.svg'
  const resolvedLogoAlt = 'Craft Home Remodeling'
  const resolvedHeading = "Let's Craft Your Home Together"
  const resolvedSubHeading = 'Craft Home Remodeling appreciates your interest in our company, and responds to every inquiry from homeowners like you.'
  const showHeader = true
  return (
    <div
      className="flex flex-col p-[10px] rounded-[9px]"
      style={{ boxShadow: 'rgba(0, 0, 0, 0.5) 0px 0px 10px 0px', backgroundColor: '#ffffff' }}
    >
      {showHeader ? (
        <div className="flex flex-col items-center text-center">
          {resolvedLogoUrl ? (
            <div className="relative mx-auto mt-6 mb-3 h-[46px] w-[100px] max-w-full">
              <Image
                alt={resolvedLogoAlt}
                className="object-contain object-center"
                fill
                sizes="200px"
                src={resolvedLogoUrl}
              />
            </div>
          ) : null}
          {resolvedHeading ? (
            <div className="flex w-full max-w-4xl items-center justify-center gap-3 px-2 sm:gap-4">
              <div
                className="hidden md:block h-px min-w-6 flex-1 max-w-[104px] shrink"
                style={{ backgroundColor: ACCENT_BAR }}
                aria-hidden
              />
              <h2
                className={cn(
                  outfit.className,
                  'text-[24px] leading-[24px] font-semibold tracking-[1px] text-black',
                )}
              >
                {resolvedHeading}
              </h2>
              <div
                className="hidden md:block h-px min-w-6 flex-1 max-w-[104px] shrink"
                style={{ backgroundColor: ACCENT_BAR }}
                aria-hidden
              />
            </div>
          ) : null}
          <p
            className={cn(
              outfit.className,
              'mt-4 text-[16px] leading-relaxed text-[#1d1d1b] border-b border-[#009BAB] pb-3 font-thin w-full',
            )}
          >
            {resolvedSubHeading}
          </p>
        </div>
      ) : null}
      <TabsContactForm
        source="homepage-tabs-form"
        showSpacer={true}
      />
    </div>
  )
}

function CtaPanel({
  body,
  buttonLabel,
  buttonUrl,
}: {
  body: DefaultTypedEditorState | null
  buttonLabel: string | null
  buttonUrl: string | null
}) {
  return (
    <div className="flex flex-col gap-6">
      {body ? (
        <RichText
          className="max-w-none text-foreground"
          data={body}
          enableGutter={false}
          enableProse
        />
      ) : null}
      {buttonLabel && buttonUrl ? (
        buttonUrl.startsWith('/') ? (
          <div>
            <Link
              className="inline-flex items-center rounded-sm bg-[#1595a7] px-5 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90"
              href={buttonUrl}
            >
              {buttonLabel}
            </Link>
          </div>
        ) : (
          <div>
            <a
              className="inline-flex items-center rounded-sm bg-[#1595a7] px-5 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90"
              href={buttonUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              {buttonLabel}
            </a>
          </div>
        )
      ) : null}
    </div>
  )
}

function FloorplanPanel({ plans }: { plans: FloorplanPlan[] }) {
  const [active, setActive] = useState(0)
  const plan = plans[Math.min(active, plans.length - 1)]!

  return (
    <div>
      {plans.length > 1 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {plans.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setActive(i)}
              className={cn(
                'px-5 py-2 text-sm font-semibold transition-colors',
                i === active
                  ? 'bg-[#8DC9D1] text-white'
                  : 'bg-[#F7E8B1] text-[#4f5555] hover:bg-[#96969647]',
              )}
            >
              {p.planTitle || `Plan ${i + 1}`}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-10">
        {/* Left: image + info */}
        <div className="flex min-w-0 flex-1 flex-col">
          {plan.planTitle && (
            <h3 className="mb-4 text-center text-3xl font-bold text-[#1d1d1b] md:text-4xl">
              {plan.planTitle}
            </h3>
          )}
          {plan.planImageUrl && (
            <div className="relative w-full overflow-hidden bg-[#f5f5f5]">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={plan.planImageUrl}
                alt={plan.planImageAlt || plan.planTitle || 'Floor plan'}
                className="h-auto w-full object-contain"
              />
            </div>
          )}
          {plan.planInfo && (
            <div className="mt-3 border border-[#1595a7] px-4 py-2 text-center text-sm font-medium tracking-widest text-[#1595a7] uppercase">
              {plan.planInfo}
            </div>
          )}
        </div>

        {/* Right: download links */}
        {plan.planDownloads.length > 0 && (
          <div className="flex shrink-0 flex-col items-center gap-3 lg:w-[220px] lg:items-end lg:pt-16">
            {plan.planDownloads.map((dl) => (
              <a
                key={dl.id}
                href={dl.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                download={dl.fileName || true}
                className="text-center text-base font-medium text-[#1d1d1b] no-underline transition-colors hover:text-[#1595a7] lg:text-right"
              >
                {dl.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/** Videos and Matterport tabs open in a modal on click rather than swapping the
 *  inline panel — so they never make a good default "active" panel. */
function isModalTab(t: ShowcaseTabPayload): boolean {
  return (t.tabKind === 'videos' && !!t.videoSrc) || (t.tabKind === 'embed' && !!t.embedUrl)
}

type MediaModalState =
  | { type: 'video'; src: string; poster: string | null; title: string }
  | { type: 'embed'; src: string; title: string }

function FeaturedVideoPlayer({ video }: { video: FeaturedVideo }) {
  const ref = useRef<HTMLVideoElement>(null)
  // Many project clips are stored 1080x1080 but play at 9:16, and some posters are square.
  // Browsers size the element from the poster until playback, so set the true shape up front.
  // object-fill stretches those squashed square posters back to the true shape.
  const [shape, setShape] = useState<React.CSSProperties | undefined>()
  const readShape = useCallback(() => {
    const el = ref.current
    if (!el?.videoWidth || !el.videoHeight) return
    const ratio = `${el.videoWidth} / ${el.videoHeight}`
    setShape({ aspectRatio: ratio, width: `min(100%, calc(80svh * ${ratio}))` })
  }, [])
  // The metadata event can fire before hydration attaches the handler.
  useEffect(readShape, [readShape])

  return (
    <video
      ref={ref}
      aria-label={video.title}
      className="mx-auto mt-6 block h-auto max-h-[80svh] w-auto max-w-full rounded-sm bg-black object-fill shadow-md"
      controls
      onLoadedMetadata={readShape}
      playsInline
      poster={video.poster || undefined}
      preload="metadata"
      src={video.src}
      style={shape}
    />
  )
}

export function ProjectShowcaseClient({
  subtitle,
  hintText,
  featuredVideo,
  tabs,
}: ProjectShowcaseClientProps) {
  // Start on the first panel-backed tab so a leading video/3D tab doesn't render
  // its inline player before the user clicks (which would open the modal).
  const firstPanelTab = tabs.findIndex((t) => !isModalTab(t))
  const [primary, setPrimary] = useState(firstPanelTab === -1 ? 0 : firstPanelTab)
  const [secondary, setSecondary] = useState(0)
  const [modal, setModal] = useState<MediaModalState | null>(null)

  // Reset the secondary tab whenever the primary tab changes (render-phase adjustment).
  const [prevPrimary, setPrevPrimary] = useState(primary)
  if (prevPrimary !== primary) {
    setPrevPrimary(primary)
    setSecondary(0)
  }

  if (tabs.length === 0) {
    return featuredVideo ? (
      <section aria-label="Project showcase" className="mt-0 w-full">
        <FeaturedVideoPlayer video={featuredVideo} />
      </section>
    ) : null
  }

  /** Click handler for the primary tablist: media tabs pop a modal, the rest
   *  switch the inline panel. */
  function handleTabChange(index: number) {
    const t = tabs[index]
    if (!t) return
    if (t.tabKind === 'videos' && t.videoSrc) {
      setModal({ type: 'video', src: t.videoSrc, poster: t.videoPoster, title: t.label })
      return
    }
    if (t.tabKind === 'embed' && t.embedUrl) {
      setModal({ type: 'embed', src: t.embedUrl, title: t.label })
      return
    }
    setPrimary(index)
  }

  const current = tabs[primary] ?? tabs[0]!
  const showGalleryNav = current.tabKind === 'photoGallery' && current.galleryCategories.length > 1
  const allEmbedTours = tabs
    .filter((t) => t.tabKind === 'embed' && t.embedUrl)
    .map((t) => ({ url: t.embedUrl!, label: t.label }))

  return (
    <section aria-label="Project showcase" className="mt-0 w-full">
      {subtitle ? (
        <p className="text-[26px] leading-[31px] font-extralight font-outfit border-2 px-2 border-[#72c7d0] w-fit text-[rgb(0,0,0)] pl-[5px] md:my-[20px] md:text-[36px] md:leading-[43px]">
          {subtitle}
        </p>
      ) : null}

      {featuredVideo ? <FeaturedVideoPlayer video={featuredVideo} /> : null}

      {hintText ? (
        <p className="font-outfit mt-[10px] px-3 text-center text-[13px] leading-[18px] md:text-[17px] md:leading-[20px] font-medium tracking-[0.4em] text-[rgb(126,126,126)]">
          {hintText}{' '}
          <span aria-hidden className="inline-block translate-y-px">
            ▾
          </span>
        </p>
      ) : null}

      <div className={cn('w-full md:pt-[20px]', hintText ? 'mt-[20px]' : subtitle ? 'mt-8' : 'mt-6')}>
        <ProjectTabList
          tabs={tabs}
          activeIndex={primary}
          onChange={handleTabChange}
          getTabId={(tab) => `project-showcase-tab-${tab.id}`}
          getPanelId={(tab) => `project-showcase-panel-${tab.id}`}
          ariaLabel="Project sections"
        />
        <div
          className={cn(
            'mx-auto max-w-[1140px] p-[10px] xl:pl-3 2xl:p-0 2xl:pl-[20px] mt-[42px] md:mt-6',
            current.tabKind !== 'cta' && current.tabKind !== 'embed' && 'border-l-30',
          )}
          style={{ borderColor: TEAL }}
        >
          {showGalleryNav ? (
            <div className="pt-2! flex w-full md:min-h-[44px]">
              <div
                className="flex w-full flex-nowrap gap-1"
                role="tablist"
                aria-label="Gallery categories"
              >
                {current.galleryCategories.map((cat, index) => (
                  <SecondaryTabButton
                    key={cat.id}
                    id={cat.id}
                    isActive={index === secondary}
                    label={cat.label}
                    primaryTabId={current.id}
                    onSelect={() => setSecondary(index)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          <div
            className={showGalleryNav ? 'mt-[42px]' : 'min-[1440px]:pt-[20px]'}
            id={`project-showcase-panel-${current.id}`}
            role="tabpanel"
            aria-labelledby={`project-showcase-tab-${current.id}`}
          >
            {current.tabKind === 'photoGallery' ? (
              <PhotoGalleryPanel secondaryIndex={secondary} tab={current} />
            ) : null}
            {current.tabKind === 'embed' && allEmbedTours.length > 0 ? (
              <EmbedPanel tours={allEmbedTours} />
            ) : null}
            {current.tabKind === 'videos' ? (
              <VideosPanel src={current.videoSrc} poster={current.videoPoster} />
            ) : null}
            {current.tabKind === 'cta' && current.ctaContactForm ? (
              <CtaFormPanel
                form={current.ctaContactForm as unknown as import("@payloadcms/plugin-form-builder/types").Form}
                heading={current.ctaHeading}
                logoAlt={current.ctaLogoAlt}
                logoUrl={current.ctaLogoUrl}
                subHeading={current.ctaSubHeading}
              />
            ) : null}
            {current.tabKind === 'cta' && !current.ctaContactForm ? (
              <CtaPanel
                body={current.ctaBody}
                buttonLabel={current.ctaButtonLabel}
                buttonUrl={current.ctaButtonUrl}
              />
            ) : null}
            {current.tabKind === 'floorplan' ? (
              <FloorplanPanel plans={current.floorplanPlans} />
            ) : null}
          </div>
        </div>
      </div>

      {modal ? (
        <MediaModal title={modal.title} onClose={() => setModal(null)}>
          {modal.type === 'video' ? (
            <video
              src={modal.src}
              poster={modal.poster || undefined}
              controls
              autoPlay
              playsInline
              className="mx-auto max-h-[85vh] w-full rounded-sm bg-black shadow-2xl"
            />
          ) : (
            <div
              className="relative mx-auto w-full overflow-hidden rounded-sm bg-black shadow-2xl"
              style={{ aspectRatio: '16 / 9', maxHeight: '85vh' }}
            >
              <iframe
                src={modal.src}
                title={modal.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; xr-spatial-tracking; fullscreen"
                allowFullScreen
                className="absolute inset-0 h-full w-full border-0"
              />
            </div>
          )}
        </MediaModal>
      ) : null}
    </section>
  )
}
